import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { RestaurantInquiryItem, UpdateRestaurantInquiryItemPayload } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// PATCH ums/v1/cms/restaurant-inquiries/:id/items/:itemId — partial update of a
// single item. Returns just the updated item (masterProduct eager-loaded), not
// the parent inquiry; we invalidate the inquiry queries so detail/list refetch.
// 404 if itemId doesn't belong to inquiry id; 400 if masterProductId is invalid.
type TData = RestaurantInquiryItem;
type TError = AxiosError;
type TVariables = { id: number | string; itemId: number; data: UpdateRestaurantInquiryItemPayload };

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useUpdateRestaurantInquiryItemApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, itemId, data }: TVariables): Promise<TData> => {
    const response = await http.patch(`${restaurantInquiriesEndpoint}/${id}/items/${itemId}`, data);
    // The API wraps the updated item in an envelope: { data: {...} }.
    return response.data?.data ?? response.data;
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'update-item'],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
